import { Delete as DeleteIcon } from 'components/icons';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useId } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { TimecardIncrementUpdateDto } from 'types/tenants.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface TimecardIncrementsForm {
    increments: { value: string }[];
    defaultIndex: number | null;
}

interface Props {
    initialData: {
        timecardIncrementValues: number[] | null;
        defaultTimecardIncrement: number | null;
    };
    onSubmit: (dto: TimecardIncrementUpdateDto) => void;
    onClose: () => void;
}

// An increment is valid when it is strictly between 0 and 1 and a multiple of 0.1;
// 0.25 and 0.75 are additionally allowed when two-decimal timecards are enabled.
const isValidIncrement = (value: number, allowQuarters: boolean): boolean => {
    if (!Number.isFinite(value) || value <= 0 || value >= 1) {
        return false;
    }
    const cents = Math.round(value * 100);
    if (Math.abs(value * 100 - cents) > 1e-6) {
        return false;
    }
    return cents % 10 === 0 || (allowQuarters && (cents === 25 || cents === 75));
};

// Base UI's radio group steers arrow keys and focus for everything it wraps, and a radio it moves focus
// to gets checked: an arrow key in an increment input or on a remove button would change the default.
// Everything but the radios keeps the browser's own handling.
const keepGroupKeysToRadios = (event: { target: EventTarget; preventBaseUIHandler: () => void }): void => {
    if (!(event.target instanceof Element && event.target.getAttribute('role') === 'radio')) {
        event.preventBaseUIHandler();
    }
};

// A single row is always the default; the radio only becomes a real choice with two or more rows.
const toFormValues = (initialData: Props['initialData']): TimecardIncrementsForm => {
    const values = initialData.timecardIncrementValues ?? [];
    if (values.length === 0) {
        return { increments: [{ value: '' }], defaultIndex: 0 };
    }
    const defaultIndex = values.findIndex((value) => value === initialData.defaultTimecardIncrement);
    return {
        increments: values.map((value) => ({ value: String(value) })),
        defaultIndex: values.length === 1 ? 0 : defaultIndex >= 0 ? defaultIndex : null,
    };
};

const TimecardIncrementsEditModal = (props: Props) => {
    const { initialData, onClose, onSubmit } = props;
    const allowQuarters = useIsFeatureEnabled(FeatureFlag.TIMECARD_TWO_DECIMAL_POINTS_ENTRIES);
    const errorIdPrefix = useId();

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<TimecardIncrementsForm>({
        defaultValues: toFormValues(initialData),
        mode: 'onChange',
    });
    const { fields, append, remove } = useFieldArray({ control, name: 'increments' });
    const defaultIndex = useWatch({ control, name: 'defaultIndex' });

    const selectDefault = (index: number): void => {
        setValue('defaultIndex', index);
        clearErrors('defaultIndex');
    };

    const removeRow = (index: number): void => {
        const currentDefault = getValues('defaultIndex');
        remove(index);
        if (getValues('increments').length === 1) {
            setValue('defaultIndex', 0);
            clearErrors('defaultIndex');
        } else if (currentDefault === index) {
            setValue('defaultIndex', null);
        } else if (currentDefault !== null && currentDefault > index) {
            setValue('defaultIndex', currentDefault - 1);
        }
    };

    const onSave = (): void => {
        void handleSubmit((data) => {
            const defaultRow = data.defaultIndex === null ? undefined : data.increments[data.defaultIndex];
            if (!defaultRow) {
                setError('defaultIndex', {
                    type: 'validate',
                    message: t('configuration.timecard-increment-default-required'),
                });
                return;
            }
            onSubmit({
                timecardIncrementValues: data.increments.map((row) => Number(row.value)),
                defaultTimecardIncrement: Number(defaultRow.value),
            });
        })();
    };

    const canRemove = fields.length > 1;

    return (
        <>
            <ModalHeader title={t('configuration.timecard-increment-control')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div className="mt-6 flex flex-col gap-2">
                    <h6 className="text-h6">
                        <FormattedMessage id="configuration.timecard-increment-edit-title" />
                    </h6>
                    <p className="text-body2 text-fg-secondary">
                        <FormattedMessage id="configuration.timecard-increment-edit-help-text" />
                    </p>
                    {/* The group wraps the whole table: its radios sit one per row, each in its own cell. */}
                    <RadioGroup
                        value={defaultIndex}
                        onValueChange={(value) => selectDefault(value as number)}
                        onKeyDownCapture={keepGroupKeysToRadios}
                        onKeyDown={keepGroupKeysToRadios}
                        onFocus={keepGroupKeysToRadios}
                        aria-label={t('configuration.timecard-increment-default')}
                        className="border-divider block overflow-hidden rounded-[4px] border"
                    >
                        <table className="w-full text-left text-sm">
                            <TableHeader className="[&_tr]:border-b-0">
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableHead className={SIMPLE_TABLE_HEAD_CLASS}>
                                        <FormattedMessage id="configuration.timecard-increment-value" />
                                    </TableHead>
                                    <TableHead className={cn(SIMPLE_TABLE_HEAD_CLASS, 'text-center')}>
                                        <FormattedMessage id="configuration.timecard-increment-default" />
                                    </TableHead>
                                    <TableHead className={SIMPLE_TABLE_HEAD_CLASS} />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {fields.map((field, index) => (
                                    <TableRow key={field.id} className={SIMPLE_TABLE_ROW_CLASS}>
                                        <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                                            <Controller
                                                name={`increments.${index}.value`}
                                                control={control}
                                                rules={{
                                                    required: t('forms.field-is-required'),
                                                    validate: {
                                                        validIncrement: (value) =>
                                                            isValidIncrement(Number(value), allowQuarters) ||
                                                            t(
                                                                allowQuarters
                                                                    ? 'configuration.timecard-increment-invalid-value-quarters'
                                                                    : 'configuration.timecard-increment-invalid-value'
                                                            ),
                                                        noDuplicate: (value, formValues) =>
                                                            formValues.increments.filter(
                                                                (row) => Number(row.value) === Number(value)
                                                            ).length <= 1 ||
                                                            t('configuration.timecard-increment-duplicate-value'),
                                                    },
                                                }}
                                                render={({ field: valueField }) => {
                                                    const error = errors.increments?.[index]?.value;
                                                    const errorId = `${errorIdPrefix}-${index}-error`;
                                                    return (
                                                        <div className="w-40">
                                                            <Input
                                                                {...valueField}
                                                                onChange={(e) => {
                                                                    // Commas are typed as decimal separators in some locales; store them as dots.
                                                                    const value = e.target.value.replace(/,/g, '.');
                                                                    if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
                                                                        valueField.onChange(value);
                                                                    }
                                                                }}
                                                                inputMode="decimal"
                                                                aria-label={t('configuration.timecard-increment-value')}
                                                                aria-invalid={!!error}
                                                                aria-describedby={error?.message ? errorId : undefined}
                                                                className="bg-paper"
                                                            />
                                                            <FieldError
                                                                id={errorId}
                                                                className="text-caption text-error mx-3.5 mt-[3px]"
                                                                errors={[error]}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                                            <RadioGroupItem
                                                value={index}
                                                aria-label={t('configuration.timecard-increment-default')}
                                                className="mx-auto"
                                            />
                                        </TableCell>
                                        <TableCell className={cn(SIMPLE_TABLE_CELL_CLASS, 'text-right')}>
                                            {canRemove ? (
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    className="text-destructive rounded-full"
                                                    aria-label={t('configuration.timecard-increment-remove-value')}
                                                    onClick={() => removeRow(index)}
                                                >
                                                    <DeleteIcon />
                                                </Button>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </table>
                    </RadioGroup>
                    {errors.defaultIndex ? (
                        <p className="text-body2 text-fg-error">{errors.defaultIndex.message}</p>
                    ) : null}
                    <div>
                        <Button
                            {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
                            onClick={() => append({ value: '' })}
                        >
                            <FormattedMessage id="configuration.timecard-increment-add-value" />
                        </Button>
                    </div>
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} />
        </>
    );
};

export default TimecardIncrementsEditModal;
